import { GachasConnectionFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { GachaBox } from '@/components/organisms/gacha/GachaBox'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  artistId: string
  artistName: string
  artistGachas: Maybe<GachasConnectionFragment>
  liveStreamId: string | undefined
  user: Maybe<SideMenuProps['user']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ArtistGachasTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistGachas')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        header={{ title: t('title') }}
        margin
      >
        <Head title={[routerNames.ArtistGachas, state.artistName]} />
        <LayoutAreaCell
          artistId={state.artistId}
          artistGachas={state.artistGachas}
          hasNextPage={state.hasNextPage}
          loading={state.loading}
          onFetchMore={onFetchMore}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const LayoutAreaCell: Cell<{
  loading: boolean
  hasNextPage: boolean
  artistId: string
  artistGachas: Maybe<GachasConnectionFragment>
  onFetchMore: () => Promise<void>
}> = ({ loading, hasNextPage, artistId, artistGachas, onFetchMore }) => {
  const { t } = useTranslation('artistGachas')

  return (
    <CellFrame sx={{ py: 2 }}>
      <ScrollFetcher
        nodes={artistGachas?.edges?.map(({ node }) => (
          <GachaBox key={node.id} gacha={node} artistId={artistId} fullWidth />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 5 }}
        emptyMessage={t('gachaEmptyMessage')}
      />
    </CellFrame>
  )
}
